/**
 * React Query hooks for Dashboard Analytics
 * Provides caching, background updates, and error handling
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dashboardService } from '../services/dashboardService';
import type { DateRange, AggregatedDashboardData } from '../services/dashboardService';

// Query keys for consistent caching
export const dashboardKeys = {
  all: ['dashboard'] as const,
  overview: (dateRange?: DateRange) => ['dashboard', 'overview', dateRange] as const,
  clientBreakdown: (dateRange?: DateRange) => ['dashboard', 'client-breakdown', dateRange] as const,
  fitmentDistribution: (dateRange?: DateRange) => ['dashboard', 'fitment-distribution', dateRange] as const,
  recruiterPerformance: (dateRange?: DateRange) => ['dashboard', 'recruiter-performance', dateRange] as const,
  timeSeries: (dateRange?: DateRange) => ['dashboard', 'time-series', dateRange] as const,
  requirements: (dateRange?: DateRange) => ['dashboard', 'requirements', dateRange] as const,
  managerPerformance: (dateRange?: DateRange) => ['dashboard', 'manager-performance', dateRange] as const,
  comprehensive: (dateRange?: DateRange) => ['dashboard', 'comprehensive', dateRange] as const,
  aggregated: (dateRange?: DateRange) => ['dashboard', 'aggregated', dateRange] as const,
  cacheStats: () => ['dashboard', 'cache-stats'] as const,
};

/**
 * Hook for dashboard overview metrics
 * Cache: 1 minute for real-time updates
 */
export const useDashboardOverview = (dateRange?: DateRange) => {
  return useQuery({
    queryKey: dashboardKeys.overview(dateRange),
    queryFn: () => dashboardService.getOverview(dateRange),
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });
};

/**
 * Hook for client breakdown analytics
 * Cache: 5 minutes for complex aggregations
 */
export const useClientBreakdown = (dateRange?: DateRange) => {
  return useQuery({
    queryKey: dashboardKeys.clientBreakdown(dateRange),
    queryFn: () => dashboardService.getClientBreakdown(dateRange),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook for fitment score distribution
 * Cache: 5 minutes for aggregations
 */
export const useFitmentScoreDistribution = (dateRange?: DateRange) => {
  return useQuery({
    queryKey: dashboardKeys.fitmentDistribution(dateRange),
    queryFn: () => dashboardService.getFitmentScoreDistribution(dateRange),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook for recruiter performance analytics
 * Cache: 5 minutes for complex aggregations
 */
export const useRecruiterPerformance = (dateRange?: DateRange) => {
  return useQuery({
    queryKey: dashboardKeys.recruiterPerformance(dateRange),
    queryFn: () => dashboardService.getRecruiterPerformance(dateRange),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook for time series data
 * Cache: 5 minutes for time series data
 */
export const useTimeSeriesData = (dateRange?: DateRange) => {
  return useQuery({
    queryKey: dashboardKeys.timeSeries(dateRange),
    queryFn: () => dashboardService.getTimeSeriesData(dateRange),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook for requirements analytics
 * Cache: 5 minutes for complex aggregations
 */
export const useRequirementsAnalytics = (dateRange?: DateRange) => {
  return useQuery({
    queryKey: dashboardKeys.requirements(dateRange),
    queryFn: () => dashboardService.getRequirementsAnalytics(dateRange),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook for manager performance analytics
 * Cache: 5 minutes for aggregations
 */
export const useManagerPerformance = (dateRange?: DateRange) => {
  return useQuery({
    queryKey: dashboardKeys.managerPerformance(dateRange),
    queryFn: () => dashboardService.getManagerPerformance(dateRange),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook for comprehensive analytics (all data in one request)
 * Optimized for initial dashboard load
 */
export const useComprehensiveAnalytics = (dateRange?: DateRange) => {
  return useQuery({
    queryKey: dashboardKeys.comprehensive(dateRange),
    queryFn: () => dashboardService.getComprehensiveAnalytics(dateRange),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });
};

/**
 * Hook for aggregated dashboard data with AI insights
 * Returns pre-aggregated metrics with AI-powered insights
 */
export const useAggregatedDashboard = (dateRange?: DateRange) => {
  return useQuery<AggregatedDashboardData>({
    queryKey: dashboardKeys.aggregated(dateRange),
    queryFn: () => dashboardService.getAggregatedDashboard(dateRange),
    staleTime: 5 * 60 * 1000, // 5 minutes (aggregated data updates daily)
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook for cache statistics (admin only)
 */
export const useCacheStats = () => {
  return useQuery({
    queryKey: dashboardKeys.cacheStats(),
    queryFn: () => dashboardService.getCacheStats(),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * Hook for clearing dashboard cache
 */
export const useClearDashboardCache = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => dashboardService.clearCache(),
    onSuccess: () => {
      // Invalidate all dashboard queries
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
    onError: (error) => {
      console.error('Failed to clear dashboard cache:', error);
    },
  });
};

/**
 * Hook for refreshing all dashboard data
 */
export const useRefreshDashboard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Invalidate all dashboard queries to force refetch
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
    onError: (error) => {
      console.error('Failed to refresh dashboard:', error);
    },
  });
};

/**
 * Hook for prefetching dashboard data
 * Useful for preloading data before user navigates to dashboard
 */
export const usePrefetchDashboard = () => {
  const queryClient = useQueryClient();

  const prefetchComprehensive = (dateRange?: DateRange) => {
    queryClient.prefetchQuery({
      queryKey: dashboardKeys.comprehensive(dateRange),
      queryFn: () => dashboardService.getComprehensiveAnalytics(dateRange),
      staleTime: 2 * 60 * 1000, // 2 minutes
    });
  };

  const prefetchOverview = (dateRange?: DateRange) => {
    queryClient.prefetchQuery({
      queryKey: dashboardKeys.overview(dateRange),
      queryFn: () => dashboardService.getOverview(dateRange),
      staleTime: 60 * 1000, // 1 minute
    });
  };

  return {
    prefetchComprehensive,
    prefetchOverview,
  };
};

/**
 * Utility hook for dashboard data loading states
 */
export const useDashboardLoadingStates = (dateRange?: DateRange) => {
  const overview = useDashboardOverview(dateRange);
  const clientBreakdown = useClientBreakdown(dateRange);
  const fitmentDistribution = useFitmentScoreDistribution(dateRange);
  const recruiterPerformance = useRecruiterPerformance(dateRange);
  const timeSeries = useTimeSeriesData(dateRange);
  const requirements = useRequirementsAnalytics(dateRange);
  const managerPerformance = useManagerPerformance(dateRange);

  return {
    isLoading: 
      overview.isLoading ||
      clientBreakdown.isLoading ||
      fitmentDistribution.isLoading ||
      recruiterPerformance.isLoading ||
      timeSeries.isLoading ||
      requirements.isLoading ||
      managerPerformance.isLoading,
    
    isError: 
      overview.isError ||
      clientBreakdown.isError ||
      fitmentDistribution.isError ||
      recruiterPerformance.isError ||
      timeSeries.isError ||
      requirements.isError ||
      managerPerformance.isError,
    
    errors: {
      overview: overview.error,
      clientBreakdown: clientBreakdown.error,
      fitmentDistribution: fitmentDistribution.error,
      recruiterPerformance: recruiterPerformance.error,
      timeSeries: timeSeries.error,
      requirements: requirements.error,
      managerPerformance: managerPerformance.error,
    }
  };
};
